import React from 'react';

const CustomBarChart = ({ 
  data = [], 
  title = '', 
  xKey = 'name', 
  yKey = 'value',
  nameKey = null,
  dataKey = null,
  color = 'var(--accent)',
  maxHeight = 'max-h-80',
  showValues = true,
  sortBy = 'value',
  sortOrder = 'desc',
  allowLabelWrap = false,
  maxLabelLines = 2,
  showLabelTooltip = true,
  className = ''
}) => {
  const sortedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return [...data].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      
      if (sortOrder === 'desc') {
        return bValue - aValue;
      }
      return aValue - bValue;
    });
  }, [data, sortBy, sortOrder]);

  const resolvedXKey = nameKey || xKey;
  const resolvedYKey = dataKey || yKey;

  const maxValue = Math.max(...sortedData.map(item => item[resolvedYKey] || 0));

  return (
    <div className={`bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)]/90 shadow-[0_8px_28px_rgba(15,23,42,0.08)] p-5 sm:p-6 ${className}`}>
      {title && (
        <h3 className="text-base sm:text-lg font-semibold tracking-tight text-[color:var(--text-primary)] mb-4">{title}</h3>
      )}
      
      <div className={`${maxHeight} overflow-y-auto`}>
        <div className="space-y-2.5">
          {sortedData.map((item, index) => {
            const value = item[resolvedYKey] || 0;
            const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
            const rawLabel = item[resolvedXKey];
            const label = rawLabel === null || rawLabel === undefined ? '' : String(rawLabel);
            const wrappedLabelStyle = allowLabelWrap ? {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: maxLabelLines,
              overflow: 'hidden',
              lineHeight: 1.3
            } : undefined;
            
            return (
              <div key={index} className="flex items-center space-x-3 rounded-lg px-2 py-1.5 transition-colors duration-200 hover:bg-[color:var(--surface-muted)]/70">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1.5 gap-2">
                    <span
                      className={`text-sm font-medium text-[color:var(--text-primary)] ${allowLabelWrap ? '' : 'truncate'}`}
                      style={wrappedLabelStyle}
                      title={showLabelTooltip ? label : undefined}
                    >
                      {label}
                    </span>
                    {showValues && (
                      <span className="text-sm font-bold text-[color:var(--text-primary)] ml-2 tabular-nums shrink-0">
                        {value.toLocaleString('pt-BR')}
                      </span>
                    )}
                  </div>
                  <div className="w-full bg-[color:var(--surface-muted)]/90 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full transition-all duration-300"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: color,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16)'
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {sortedData.length === 0 && (
        <div className="text-center py-8 text-[color:var(--text-muted)]">
          Nenhum dado disponível
        </div>
      )}
    </div>
  );
};

export default CustomBarChart;
